import {
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityRecord,
  SkippedDestinationEntityRecord,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import { type WithRef, getError, toTimestamp } from '@principle-theorem/shared';
import { DestinationEntity } from '../../../destination/destination-entity';
import { getBucketStoragePath, CopyFiles } from '../../../storage';
import { PatientSourceEntity } from '../../source/entities/patients';
import {
  IPatientDocumentJobData,
  type IPatientFileData,
  IPatientFileMigrationData,
  PatientDocumentDestinationEntity,
} from './patient-documents';
import { PatientDestinationEntity } from './patients';
import { IPatient } from '@principle-theorem/principle-core/interfaces';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import { TranslationMapHandler } from '../../../translation-map';

const imageAllowedTypes = ['jpeg', 'jpg', 'png'];

export const PATIENT_XRAYS_RESOURCE_TYPE = 'patientXrays';

export const PATIENT_XRAYS_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: PATIENT_XRAYS_RESOURCE_TYPE,
    label: 'Patient X-Rays',
    description: 'Patient X-Rays',
  },
});

export class PatientXrayDestinationEntity extends PatientDocumentDestinationEntity {
  override destinationEntity = PATIENT_XRAYS_DESTINATION_ENTITY;
  override sourceCountComparison = new PatientSourceEntity();

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  override async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientDocumentJobData
  ): Promise<
    | IPatientFileMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
    | (IDestinationEntityRecord & SkippedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = data.sourcePatient.data.id;
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId.toString(),
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this.buildErrorResponse(
          data.sourcePatient,
          `Couldn't resolve patient`
        );
      }

      const documents = await this.getPatientFiles(
        migration,
        sourcePatientId.toString()
      );

      if (!documents.length) {
        return this.buildSkippedResponse(data);
      }

      return {
        sourcePatientId: sourcePatientId.toString(),
        patientRef,
        documents,
        letters: [],
      };
    } catch (error) {
      return this.buildErrorResponse(data.sourcePatient, getError(error));
    }
  }

  override async getPatientFiles(
    migration: WithRef<IPracticeMigration>,
    patientId: string
  ): Promise<IPatientFileData[]> {
    const patientFolderPath = patientId.padStart(6, '0');
    const projectId = migration.configuration.projectId;
    const bucketName = getBucketStoragePath(migration.source);
    const bucketPath = `images/${patientFolderPath}`;

    const storage = new CopyFiles(bucketName, bucketPath, '', '', projectId)
      .storage;

    const [files] = await storage.bucket(bucketName).getFiles({
      prefix: bucketPath,
    });

    return files
      .filter((file) => {
        const extension = file.name.split('.').pop();
        return extension
          ? imageAllowedTypes.includes(extension.toLowerCase())
          : false;
      })
      .map((file) => ({
        name: file.name.split('/').pop() ?? file.name,
        filePath: file.name,
        createdAt: toTimestamp(),
      }));
  }
}
